import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

interface AuthUser {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
  emailVerified?: boolean;
  phoneNumber?: string;
  role?: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  user: AuthUser | null;
  loading: boolean;
  getToken: () => Promise<string | null>;
  loginWithGoogle: (credential: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  registerWithEmail: (data: {
    fullName: string;
    email: string;
    password: string;
    phoneNumber?: string;
  }) => Promise<RegisterResult>;
  resendVerification: (email: string) => Promise<RegisterResult>;
  signOut: () => void;
}

interface RegisterResult {
  ok: boolean;
  requiresVerification?: boolean;
  emailSent?: boolean;
  activationUrl?: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = "esoteric_token";
const USER_KEY = "esoteric_user";

async function readApiError(res: Response) {
  const text = await res.text();
  let body: { error?: string; code?: string } = {};
  try {
    if (text.trimStart().startsWith("{")) {
      body = JSON.parse(text);
    }
  } catch {
    /* ignore */
  }
  const err = new Error(body.error ?? "Error al autenticar") as Error & {
    status?: number;
    code?: string;
  };
  err.status = res.status;
  err.code = body.code;
  throw err;
}

async function parseJsonResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const trimmed = text.trimStart();
  if (!trimmed || trimmed.startsWith("<")) {
    throw new Error("El servidor respondió HTML en lugar de JSON");
  }
  return JSON.parse(text) as T;
}

function toAuthUser(data: {
  user?: {
    id?: string;
    email?: string;
    fullName?: string | null;
    avatarUrl?: string | null;
    emailVerified?: boolean;
    phoneNumber?: string | null;
    role?: string;
  };
}) {
  return {
    sub: data.user?.id ?? "",
    email: data.user?.email,
    name: data.user?.fullName ?? undefined,
    picture: data.user?.avatarUrl ?? undefined,
    emailVerified: data.user?.emailVerified,
    phoneNumber: data.user?.phoneNumber ?? undefined,
    role: data.user?.role,
  };
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    if (stored && storedUser) {
      try {
        setToken(stored);
        setUser(JSON.parse(storedUser));
      } catch { /* expired or corrupt */ }
    }
    setLoading(false);
  }, []);

  const loginWithGoogle = useCallback(async (credential: string) => {
    const res = await fetch("/api/auth/google", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ credential }),
    });

    if (!res.ok) {
      await readApiError(res);
    }

    const data = await parseJsonResponse<{
      token: string;
      user?: Parameters<typeof toAuthUser>[0]["user"];
    }>(res);
    const userData: AuthUser = toAuthUser(data);

    localStorage.setItem(STORAGE_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setToken(data.token);
    setUser(userData);
  }, []);

  const loginWithPassword = useCallback(async (email: string, password: string) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      await readApiError(res);
    }

    const data = await parseJsonResponse<{
      token: string;
      user?: Parameters<typeof toAuthUser>[0]["user"];
    }>(res);
    const userData: AuthUser = toAuthUser(data);

    localStorage.setItem(STORAGE_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setToken(data.token);
    setUser(userData);
  }, []);

  const registerWithEmail = useCallback(
    async (data: { fullName: string; email: string; password: string; phoneNumber?: string }) => {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        await readApiError(res);
      }

      return parseJsonResponse<RegisterResult>(res);
    },
    []
  );

  const resendVerification = useCallback(async (email: string) => {
    const res = await fetch("/api/auth/resend-verification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    if (!res.ok) {
      await readApiError(res);
    }

    return parseJsonResponse<RegisterResult>(res);
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    return token;
  }, [token]);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token,
        user,
        loading,
        getToken,
        loginWithGoogle,
        loginWithPassword,
        registerWithEmail,
        resendVerification,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
};
